import * as chmln from "@chamaeleonidae/chmln";
import { LoginResponseBodyUserType } from "@intelligems/ig-types";
import * as Sentry from "@sentry/react";
import { mpLogin, mpLogout } from "@src/utils/mixpanel";

/**
 * Identifying the user to an analytics provider is best-effort and must never
 * abort the caller. `setUser` runs inside the login query's `meta.onSuccess`,
 * so anything thrown here unwinds out of the react-query cache callback with
 * nothing to catch it and surfaces as an unhandled app crash.
 *
 * Third-party SDK globals are the realistic failure source, because they are
 * loaded and replaced at runtime. Chameleon is the one that has failed in
 * production (Sentry WEB-UI-969): the loaded SDK throws from inside its own
 * `identify`. Mixpanel ships bundled from npm, so it has none of that
 * failure mode — it is wrapped for uniformity.
 *
 * Failures are reported to Sentry as handled exceptions tagged with the
 * provider, so a provider that starts failing stays visible instead of being
 * swallowed — it just no longer takes the session down with it.
 */
const identifyWithProvider = (provider: string, identify: () => void) => {
  try {
    identify();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { analyticsProvider: provider },
      level: "warning",
    });
  }
};

export const setUser = (userResponse: LoginResponseBodyUserType | null) => {
  if (!userResponse) {
    Sentry.setUser(null);

    identifyWithProvider("mixpanel", () => {
      mpLogout();
    });

    return;
  }

  const { user, organization } = userResponse;

  const name =
    [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;

  Sentry.setUser({
    id: user.id,
    username: user.email,
    email: user.email,
    name,
  });

  identifyWithProvider("clarity", () => {
    if (!window.clarity) {
      return;
    }

    window.clarity("identify", user.id, undefined, undefined, user.email);
    window.clarity("set", "orgId", organization.id);
  });

  identifyWithProvider("mixpanel", () => {
    mpLogin(userResponse);
  });

  identifyWithProvider("chameleon", () => {
    chmln.identify(user.id, {
      email: user.email,
      name,
      uid_hash: userResponse.chameleonHmac,
      // Associate the user to their org as a Chameleon company (uid = org id) so tours
      // can target company traits fed by the daily Snowflake -> Chameleon sync. Top-level
      // organization.id is the active-session org and matches the sync's company uid.
      company: {
        uid: organization.id,
      },
    });
  });
};
